import { create } from 'zustand';
import { DatabaseService } from './DatabaseService';
import { IPlant as PowerStation } from 'pages/thermography/model/plant.model';
import { AnomalyRecord } from 'data/entities/AnomalyRecord';
import { MapMarkerPoint } from 'pages/thermography/modules/mapMarkerPoints';
import { getDeadlineLabels, getMonitoringLabels, parseDate } from '../Utils';
import { InspectionRoute } from 'data/entities/InspectionRoute';
import dayjs from 'dayjs';
import { IInspectionRecord } from '../model/inspection-record.model';

export interface PowerStationFilterState {

    mapMarkerPoints: MapMarkerPoint[];
    powerStations: PowerStation[];
    anomalyRecords: AnomalyRecord[];
    anomalyRecordsMonitoring: AnomalyRecord[];
    activeAnomalyRecords: AnomalyRecord[];
    thermographicRecords: AnomalyRecord[];

    //Filters
    searchPowerStationsFilter: string;
    conditionFilter: string[];
    deadlineFilter: string[];
    monitoringFilter: string[];
    inspectionRouteFilter: string[];

    setPowerStations: (list: PowerStation[]) => void;
    setMapMarkerPoints: (list: MapMarkerPoint[]) => void;
    setAnomalyRecords: (list: AnomalyRecord[]) => void;
    setActiveAnomalyRecords: (list: AnomalyRecord[]) => void;
    setAnomalyRecordsMonitoring: (list: AnomalyRecord[]) => void;
    setThermographicRecords: (list: AnomalyRecord[]) => void;

    refreshData: () => void;
    filterPowerStations: (searchTerm: string) => void;
    filterAnomalyRecords: (filters: string[]) => void;

    filterActiveAnomalyRecords: () => void;
    filterActiveAnomalyRecordsByCondition: (filters: string[]) => void;
    filterActiveAnomalyRecordsByDeadline: (filters: string[]) => void;
    filterActiveAnomalyRecordsByMonitoring: (filters: string[]) => void;

    inspectionRoutes: InspectionRoute[];
    setInspectionRoutes: (list: InspectionRoute[]) => void;

    inspectionRecords: IInspectionRecord[];
    setInspectionRecords: (list: IInspectionRecord[]) => void;
}

export const usePowerStationFilterStore = create<PowerStationFilterState>((set, get) => {
    // const dbService = DatabaseService.getInstance();

    return {
        mapMarkerPoints: [],
        powerStations: [],
        anomalyRecords: [],
        anomalyRecordsMonitoring: [],
        thermographicRecords: [],

        searchPowerStationsFilter: '',
        conditionFilter: [],
        deadlineFilter: [],
        monitoringFilter: [],
        inspectionRouteFilter: [],

        activeAnomalyRecords: [],
        inspectionRoutes: [],
        inspectionRecords: [],

        setMapMarkerPoints: (list) => set({ mapMarkerPoints: list }),

        setPowerStations: (list) => set({ powerStations: list }),

        setAnomalyRecords: (list) => set({ anomalyRecords: list }),

        setActiveAnomalyRecords: (list) => set({ activeAnomalyRecords: list }),

        setAnomalyRecordsMonitoring: (list) => set({ anomalyRecordsMonitoring: list }),

        setThermographicRecords: (list) => set({ thermographicRecords: list }),

        setInspectionRoutes: (list) => set({ inspectionRoutes: list }),
        setInspectionRecords: (list) => set({ inspectionRecords: list }),

        refreshData: () => {
            const dbService = DatabaseService.getInstance();
            dbService.parseAnomalyRecords();
            set(() => ({
                mapMarkerPoints: dbService.getMapMarkerPoints(),
                powerStations: dbService.getPowerStations(),
                anomalyRecords: dbService.getAnomalyRecords(),
                anomalyRecordsMonitoring: dbService.getAnomalyRecordsMonitoring(),
                activeAnomalyRecords: dbService.getActiveAnomalyRecords(),
                inspectionRoutes: dbService.getInspectionRoutes(),
                inspectionRecords: dbService.getInspectionRecords(),
                thermographicRecords: dbService.getThermographicRecords(),
            }))
        },

        filterAnomalyRecords: (filters: string[]) => {
            //************************************************************************ */
            const powerStationsFiltered = get().powerStations.filter((ps) =>
                ps.code?.toLowerCase().includes(get().searchPowerStationsFilter.toLowerCase())
            );

            const ids = new Set(powerStationsFiltered.map(item => item.id));

            const anomalyRecordsFiltered = get().anomalyRecords
                .filter(item => item.plant && ids.has(item.plant.id));

            const anomalyRecordsMonitoringFiltered = get().anomalyRecordsMonitoring
                .filter(item => item.plant && ids.has(item.plant.id));
            //************************************************************************ */

            if (filters.length != 0) {
                const conditions = new Set(filters);
                set({ anomalyRecords: anomalyRecordsFiltered.filter(item => item.condition && conditions.has(item.condition)) });
                set({ anomalyRecordsMonitoring: anomalyRecordsMonitoringFiltered.filter(item => item.condition && conditions.has(item.condition)) });
            }
            else {
                set({ anomalyRecords: anomalyRecordsFiltered });
                set({ anomalyRecordsMonitoring: anomalyRecordsMonitoringFiltered });
            }
        },

        /****************************************** ACTIVE FILTERS *********************************************/

        filterActiveAnomalyRecords: () => {

            get().refreshData();
            let activeAnomalyRecordsFiltered = get().activeAnomalyRecords;

            //******************* Filter by Power Stations search term **********************
            if (get().searchPowerStationsFilter.length > 0) {
                const mapMarkerPointsFiltered = get().mapMarkerPoints.filter((ps) =>
                    ps.code.toLowerCase().includes(get().searchPowerStationsFilter.toLowerCase())
                );
                set({ mapMarkerPoints: mapMarkerPointsFiltered });

                const powerStationsFiltered = get().powerStations.filter((ps) =>
                    ps.code?.toLowerCase().includes(get().searchPowerStationsFilter.toLowerCase())
                );
                set({ powerStations: powerStationsFiltered });


                const ids = new Set(powerStationsFiltered.map(item => item.id));
                activeAnomalyRecordsFiltered = activeAnomalyRecordsFiltered.filter(item =>
                    item.plant && ids.has(item.plant.id));
            }


            //******************* Filter by Condition **********************
            if (get().conditionFilter.length > 0) {
                const conditions = new Set(get().conditionFilter);
                activeAnomalyRecordsFiltered = activeAnomalyRecordsFiltered.filter(item => item.condition && conditions.has(item.condition));
            }

            //***************** Filter by Deadlines ************************
            if (get().deadlineFilter.length > 0) {
                const dataDeadline = getDeadlineLabels();

                const deadlines = new Set(get().deadlineFilter);

                activeAnomalyRecordsFiltered = activeAnomalyRecordsFiltered.filter(x => !x.finished);

                activeAnomalyRecordsFiltered = activeAnomalyRecordsFiltered.filter(item => {
                    const dateLimit = item.deadlineExecution ? dayjs(item.deadlineExecution) : null; // Ensure it's a Dayjs instance
                    if (!dateLimit || !dateLimit.isValid()) {
                        // Invalid date, exclude from filter or handle as needed
                        return false;
                    }
                    const diffDays = dateLimit.diff(dayjs(), 'day'); // Calculate difference in days

                    //Expired
                    if (diffDays < 0 && deadlines.has(dataDeadline[0]))
                        return true;

                    //<= 30 days to expire
                    if (diffDays >= 0 && diffDays <= 30 && deadlines.has(dataDeadline[1]))
                        return true;

                    //> 30 days to expire
                    if (diffDays > 30 && deadlines.has(dataDeadline[2]))
                        return true;

                    return false;
                });
            }

            //***************** Filter by Monitoring ************************
            if (get().monitoringFilter.length > 0) {
                const dataMonitoring = getMonitoringLabels();

                const monitorings = new Set(get().monitoringFilter);

                activeAnomalyRecordsFiltered = activeAnomalyRecordsFiltered.filter(x => !x.finished);

                activeAnomalyRecordsFiltered = activeAnomalyRecordsFiltered.filter(item => {
                    const nextMonitoring = item.nextMonitoring ? dayjs(item.nextMonitoring) : null;
                    if (!nextMonitoring || !nextMonitoring.isValid()) {
                        // Invalid date, exclude from filter or handle as needed
                        return false;
                    }
                    const diffDays = nextMonitoring.diff(dayjs(), 'day'); // Calculate difference in days

                    //Expired
                    if (diffDays < 0 && monitorings.has(dataMonitoring[0]))
                        return true;

                    //<= 30 days to expire
                    if (diffDays >= 0 && diffDays <= 30 && monitorings.has(dataMonitoring[1]))
                        return true;

                    //> 30 days to expire
                    if (diffDays > 30 && monitorings.has(dataMonitoring[2]))
                        return true;

                    return false;
                });
            }

            set({ activeAnomalyRecords: activeAnomalyRecordsFiltered });
        },

        filterPowerStations: (searchTerm) => {
            set({ searchPowerStationsFilter: searchTerm });
            get().filterActiveAnomalyRecords();
        },

        filterActiveAnomalyRecordsByCondition: (filters: string[]) => {
            set({ conditionFilter: filters });
            get().filterActiveAnomalyRecords();
        },

        filterActiveAnomalyRecordsByDeadline: (filters: string[]) => {
            set({ deadlineFilter: filters });
            get().filterActiveAnomalyRecords();
        },

        filterActiveAnomalyRecordsByMonitoring: (filters: string[]) => {
            set({ monitoringFilter: filters });
            get().filterActiveAnomalyRecords();
        },
    };
});
